import React, { useEffect, useRef, useState } from 'react';

// ===== Endpoints (via backend proxy) =====
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const TRANSLATE_ENDPOINT = `${API_BASE}/swahili/translate`;
const TTS_SWAHILI_ENDPOINT = `${API_BASE}/swahili/tts/swahili`;
const TTS_FRENCH_ENDPOINT = `${API_BASE}/swahili/tts/french`;
const ASR_ENDPOINT = (lang) => `${API_BASE}/swahili/asr?lang=${encodeURIComponent(lang)}`;

const LANG_META = {
  fra_Latn: { label: 'French', flag: '🇫🇷', short: 'FR' },
  swh_Latn: { label: 'Swahili', flag: '🇰🇪', short: 'SW' },
};

// Default French TTS voice used when target is French
const FRENCH_TTS_SPEAKER = 'remy';

function extractTranslationFromResponse(data) {
  if (typeof data === 'string') return data.trim() || null;
  if (!data || typeof data !== 'object') return null;
  const keys = ['translation', 'translated_text', 'translatedText', 'result', 'output', 'text', 'response'];
  for (const key of keys) {
    const v = data[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  for (const nested of ['result', 'data']) {
    if (data[nested] && typeof data[nested] === 'object') {
      for (const key of ['translation', 'translated_text', 'translatedText', 'text', 'output']) {
        const v = data[nested][key];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
  }
  return null;
}

const SwahiliFrenchConversation = () => {
  const [sourceLang, setSourceLang] = useState('swh_Latn');
  const targetLang = sourceLang === 'swh_Latn' ? 'fra_Latn' : 'swh_Latn';

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('');
  const [turns, setTurns] = useState([]); // { id, sourceLang, targetLang, sourceAudioUrl, sourceText, targetAudioUrl, targetText }

  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const turnsEndRef = useRef(null);

  useEffect(() => {
    turnsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  useEffect(() => {
    return () => {
      // Cleanup blob URLs
      turns.forEach((t) => {
        if (t.sourceAudioUrl) URL.revokeObjectURL(t.sourceAudioUrl);
        if (t.targetAudioUrl) URL.revokeObjectURL(t.targetAudioUrl);
      });
      stopRecordingInternal();
    };
  }, []);

  const stopRecordingInternal = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {}
    }
    const stream = recordingStreamRef.current;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    recordingStreamRef.current = null;
  };

  const startRecording = async () => {
    if (isProcessing) return;
    try {
      recordedChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const stream2 = recordingStreamRef.current;
        if (stream2) stream2.getTracks().forEach((t) => t.stop());
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;
        setIsRecording(false);
        await runPipeline(blob);
      };

      recorder.start();
      setIsRecording(true);
      setStatus('Listening...');
    } catch (err) {
      console.error('Mic error:', err);
      setStatus('Cannot access microphone.');
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      setStatus('Transcribing...');
      try {
        recorder.stop();
      } catch {}
    }
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const runPipeline = async (audioBlob) => {
    const currentSource = sourceLang;
    const currentTarget = targetLang;
    const turnId = Date.now() + '-' + Math.random().toString(36).slice(2, 7);
    const sourceAudioUrl = URL.createObjectURL(audioBlob);

    // Optimistic insert
    setTurns((prev) => [
      ...prev,
      {
        id: turnId,
        sourceLang: currentSource,
        targetLang: currentTarget,
        sourceAudioUrl,
        sourceText: '',
        targetAudioUrl: null,
        targetText: '',
        loading: true,
      },
    ]);

    setIsProcessing(true);
    try {
      // 1. ASR
      setStatus('Transcribing...');
      const form = new FormData();
      const filename = currentSource === 'swh_Latn' ? 'swahili.webm' : 'french.webm';
      form.append('file', audioBlob, filename);
      const asrRes = await fetch(ASR_ENDPOINT(currentSource), {
        method: 'POST',
        body: form,
      });
      if (!asrRes.ok) throw new Error('ASR failed');
      const asrData = await asrRes.json();
      const sourceText = (asrData?.text || '').trim();
      if (!sourceText) {
        updateTurn(turnId, { sourceText: '(no speech detected)', loading: false });
        setStatus('No speech detected.');
        return;
      }
      updateTurn(turnId, { sourceText });

      // 2. Translate
      setStatus('Translating...');
      const trRes = await fetch(TRANSLATE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sourceText,
          src_lang: currentSource,
          tgt_lang: currentTarget,
        }),
      });
      if (!trRes.ok) throw new Error('Translate failed');
      const trJson = await trRes.json().catch(() => null);
      const targetText = extractTranslationFromResponse(trJson) || '';
      updateTurn(turnId, { targetText });
      if (!targetText) {
        updateTurn(turnId, { loading: false });
        setStatus('Translation empty.');
        return;
      }

      // 3. TTS target
      setStatus('Synthesizing audio...');
      let ttsRes;
      if (currentTarget === 'swh_Latn') {
        ttsRes = await fetch(TTS_SWAHILI_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: targetText,
            normalize: true,
            return_json_metadata: false,
          }),
        });
      } else {
        ttsRes = await fetch(TTS_FRENCH_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: targetText,
            language: 'fr',
            speaker_name: FRENCH_TTS_SPEAKER,
          }),
        });
      }
      if (!ttsRes.ok) throw new Error('TTS failed');
      const ttsBlob = await ttsRes.blob();
      const targetAudioUrl = URL.createObjectURL(ttsBlob);
      updateTurn(turnId, { targetAudioUrl, loading: false });
      setStatus('');

      // Auto play target audio
      setTimeout(() => {
        const audio = document.getElementById(`target-audio-${turnId}`);
        if (audio) audio.play().catch(() => {});
      }, 80);
    } catch (err) {
      console.error('Pipeline error:', err);
      updateTurn(turnId, { loading: false });
      setStatus('Error: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateTurn = (id, patch) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const clearConversation = () => {
    turns.forEach((t) => {
      if (t.sourceAudioUrl) URL.revokeObjectURL(t.sourceAudioUrl);
      if (t.targetAudioUrl) URL.revokeObjectURL(t.targetAudioUrl);
    });
    setTurns([]);
    setStatus('');
  };

  const handleLangChange = (code) => {
    if (isRecording || isProcessing) return;
    setSourceLang(code);
  };

  const srcMeta = LANG_META[sourceLang];
  const tgtMeta = LANG_META[targetLang];

  return (
    <div style={pageStyle}>
      {/* Inline keyframes */}
      <style>{keyframes}</style>

      {/* Compact hero */}
      <div style={heroStyle}>
        <div style={heroInner}>
          <h1 style={heroTitle}>
            <span style={heroAccent}>Swahili</span>
            {' ↔ '}
            <span style={heroAccent}>French</span>
            {' '}Live Translator
          </h1>
          <div style={langSwitchRow}>
            <LangPill
              active={sourceLang === 'swh_Latn'}
              disabled={isRecording || isProcessing}
              onClick={() => handleLangChange('swh_Latn')}
            >
              🇰🇪 I speak Swahili
            </LangPill>
            <LangPill
              active={sourceLang === 'fra_Latn'}
              disabled={isRecording || isProcessing}
              onClick={() => handleLangChange('fra_Latn')}
            >
              🇫🇷 Je parle Français
            </LangPill>
          </div>
        </div>
      </div>

      {/* Conversation body */}
      <div style={bodyStyle}>
        {turns.length === 0 ? (
          <EmptyState srcMeta={srcMeta} tgtMeta={tgtMeta} />
        ) : (
          <div style={turnsWrapStyle}>
            {turns.map((t) => (
              <TurnBubbles key={t.id} turn={t} />
            ))}
            <div ref={turnsEndRef} />
          </div>
        )}
      </div>

      {/* Mic dock (inline) */}
      <div style={dockWrapStyle}>
        <div style={dockInnerStyle}>
          <div style={{ position: 'relative' }}>
            {isRecording && (
              <>
                <span style={{ ...pulseRing, animationDelay: '0s' }} />
                <span style={{ ...pulseRing, animationDelay: '0.6s' }} />
              </>
            )}
            <button
              onClick={toggleRecording}
              disabled={isProcessing && !isRecording}
              title={isRecording ? 'Stop' : 'Start recording'}
              style={micBtnStyle(isRecording, isProcessing)}
            >
              {isRecording ? (
                <span style={{ fontSize: 28 }}>⏹</span>
              ) : (
                <span style={{ fontSize: 32 }}>🎤</span>
              )}
            </button>
          </div>

          <div style={dockStatusStyle}>
            {isRecording
              ? 'Listening…'
              : isProcessing
              ? status || 'Processing…'
              : status || 'Tap to speak'}
          </div>

          {turns.length > 0 && (
            <button
              onClick={clearConversation}
              disabled={isRecording || isProcessing}
              style={dockSideBtnStyle(isRecording || isProcessing)}
            >
              Clear conversation
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Sub-components =====
const LangPill = ({ active, disabled, onClick, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '9px 18px',
      borderRadius: 999,
      border: active ? '1px solid #ED1C24' : '1px solid #e5e7eb',
      background: active ? '#ED1C24' : '#ffffff',
      color: active ? '#ffffff' : '#374151',
      fontSize: 13.5,
      fontWeight: active ? 600 : 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: 'all .2s ease',
    }}
  >
    {children}
  </button>
);

const LangChip = ({ meta, label, accent = false }) => (
  <div style={langChipStyle(accent)}>
    <div style={langChipAvatar(accent)}>{meta.flag}</div>
    <div>
      <div style={{ fontSize: 10.5, opacity: 0.7, letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{meta.label}</div>
    </div>
  </div>
);

const FlowArrow = ({ processing }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={flowDot(processing, 0)} />
    <span style={flowDot(processing, 1)} />
    <span style={flowDot(processing, 2)} />
    <span style={{ color: '#9ca3af', fontSize: 18 }}>→</span>
  </div>
);

const EmptyState = ({ srcMeta, tgtMeta }) => (
  <div style={emptyStateStyle}>
    <div style={emptyIconStyle}>🎧</div>
    <h3 style={{ margin: 0, fontSize: 20, color: '#1f2937', fontWeight: 600 }}>
      Start a conversation
    </h3>
    <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: 14, maxWidth: 420 }}>
      Tap the microphone below and speak in {srcMeta.label}. You'll instantly
      hear the translation in {tgtMeta.label}.
    </p>
  </div>
);

const TurnBubbles = ({ turn }) => {
  const src = LANG_META[turn.sourceLang];
  const tgt = LANG_META[turn.targetLang];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <BubbleRow
        side="right"
        meta={src}
        audioUrl={turn.sourceAudioUrl}
        text={turn.sourceText}
        loading={turn.loading && !turn.sourceText}
        variant="user"
      />
      <BubbleRow
        side="left"
        meta={tgt}
        audioUrl={turn.targetAudioUrl}
        text={turn.targetText}
        loading={turn.loading && !turn.targetAudioUrl}
        variant="assistant"
        audioId={`target-audio-${turn.id}`}
        autoPlay
      />
    </div>
  );
};

const BubbleRow = ({
  side,
  meta,
  audioUrl,
  text,
  loading,
  variant,
  audioId,
  autoPlay,
}) => {
  const isRight = side === 'right';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isRight ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <div style={avatarStyle(variant)}>{meta.flag}</div>
      <div style={bubbleStyle(variant)}>
        <div style={bubbleHeader}>
          <span style={{ fontWeight: 600, color: variant === 'user' ? '#ED1C24' : '#111827' }}>
            {meta.label}
          </span>
        </div>

        {audioUrl ? (
          <audio
            id={audioId}
            src={audioUrl}
            controls
            autoPlay={autoPlay}
            style={{ width: '100%', height: 36, marginTop: 8 }}
          />
        ) : (
          <div style={audioPlaceholderStyle}>
            {loading ? <TypingDots /> : '—'}
          </div>
        )}

        <div style={bubbleTranscriptStyle}>
          {text || (loading ? <TypingDots dark /> : '')}
        </div>
      </div>
    </div>
  );
};

const TypingDots = ({ dark = false }) => (
  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
    <span style={typingDot(dark, 0)} />
    <span style={typingDot(dark, 1)} />
    <span style={typingDot(dark, 2)} />
  </span>
);

// ===== Styles =====
const keyframes = `
@keyframes pulseRing {
  0%   { transform: scale(1);   opacity: 0.45; }
  80%  { transform: scale(1.8); opacity: 0; }
  100% { transform: scale(1.8); opacity: 0; }
}
@keyframes typingBounce {
  0%, 80%, 100% { transform: translateY(0);   opacity: 0.4; }
  40%           { transform: translateY(-4px); opacity: 1; }
}
@keyframes flowPulse {
  0%, 100% { opacity: 0.25; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.3); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  minHeight: 420,
  background: '#ffffff',
  borderRadius: 14,
  border: '1px solid #f1f3f5',
  overflow: 'hidden',
  fontFamily:
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  color: '#111827',
};

const heroStyle = {
  padding: '18px 20px 14px',
  background: '#ffffff',
  borderBottom: '1px solid #f1f3f5',
  color: '#111827',
};

const heroInner = {
  maxWidth: 860,
  margin: '0 auto',
  textAlign: 'center',
};

const heroBadge = {
  display: 'inline-block',
  padding: '5px 12px',
  borderRadius: 999,
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: 0.3,
  color: '#4b5563',
  marginBottom: 16,
};

const heroTitle = {
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: -0.4,
  lineHeight: 1.2,
  color: '#111827',
  textAlign: 'center',
};

const heroSubtitle = {
  margin: '10px auto 0',
  maxWidth: 520,
  fontSize: 15,
  color: '#6b7280',
  lineHeight: 1.55,
};

const heroAccent = {
  color: '#ED1C24',
  fontWeight: 600,
};

const heroFlowRow = {
  marginTop: 24,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const langSwitchRow = {
  marginTop: 12,
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

const langChipStyle = (accent) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 14px 8px 8px',
  borderRadius: 999,
  background: '#ffffff',
  border: `1px solid ${accent ? '#d1d5db' : '#e5e7eb'}`,
  color: '#111827',
});

const langChipAvatar = (accent) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
});

const flowDot = (active, i) => ({
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: active ? '#ED1C24' : '#d1d5db',
  display: 'inline-block',
  animation: active ? `flowPulse 1.2s ${i * 0.15}s ease-in-out infinite` : 'none',
});

const bodyStyle = {
  maxWidth: 760,
  width: '100%',
  margin: '0 auto',
  padding: '16px 20px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const turnsWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  animation: 'fadeInUp .3s ease',
};

const emptyStateStyle = {
  margin: 'auto 0',
  padding: '24px 20px',
  textAlign: 'center',
  background: '#fafbfc',
  border: '1px solid #f1f3f5',
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const emptyIconStyle = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  marginBottom: 10,
};

const avatarStyle = (variant) => ({
  width: 36,
  height: 36,
  minWidth: 36,
  borderRadius: '50%',
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
});

const bubbleStyle = (variant) => ({
  maxWidth: '78%',
  padding: '12px 14px',
  borderRadius: 14,
  fontSize: 14,
  lineHeight: 1.5,
  background: variant === 'user' ? '#f9fafb' : '#ffffff',
  color: '#111827',
  border: '1px solid #f1f3f5',
  animation: 'fadeInUp .25s ease',
});

const bubbleHeader = {
  fontSize: 11,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const bubbleTranscriptStyle = {
  marginTop: 8,
  color: '#374151',
  fontSize: 14,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  minHeight: 20,
};

const audioPlaceholderStyle = {
  height: 36,
  marginTop: 8,
  display: 'flex',
  alignItems: 'center',
  color: '#9ca3af',
  fontSize: 13,
};

const typingDot = (dark, i) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: dark ? '#6b7280' : '#9ca3af',
  display: 'inline-block',
  animation: `typingBounce 1.2s ${i * 0.15}s ease-in-out infinite`,
});

// Dock (compact bar at bottom of viewport-sized layout)
const dockWrapStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '12px 20px 18px',
  borderTop: '1px solid #f1f3f5',
  background: '#ffffff',
};

const dockInnerStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 14,
};

const pulseRing = {
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2px solid #ED1C24',
  animation: 'pulseRing 1.6s ease-out infinite',
  pointerEvents: 'none',
};

const micBtnStyle = (recording, processing) => ({
  position: 'relative',
  width: 58,
  height: 58,
  borderRadius: '50%',
  border: 'none',
  background: '#ED1C24',
  color: '#fff',
  cursor: processing && !recording ? 'not-allowed' : 'pointer',
  boxShadow: recording
    ? '0 6px 18px rgba(237, 28, 36, 0.45)'
    : '0 4px 12px rgba(237, 28, 36, 0.3)',
  transition: 'transform .15s ease, box-shadow .2s ease, background .2s ease',
  opacity: processing && !recording ? 0.6 : 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const dockSideBtnStyle = (disabled) => ({
  height: 32,
  padding: '0 14px',
  borderRadius: 999,
  border: '1px solid #e5e7eb',
  background: '#ffffff',
  color: '#6b7280',
  fontSize: 12.5,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  transition: 'all .15s ease',
});

const dockStatusStyle = {
  fontSize: 13,
  color: '#6b7280',
  fontWeight: 500,
  textAlign: 'center',
};

export default SwahiliFrenchConversation;

